import React, { ReactNode, ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Patterns.css';

export interface MenuItemProps {
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  children: ReactNode;
}

interface MenuItemInternalProps extends MenuItemProps {
  closeMenu?: () => void;
  itemIndex?: number;
  registerRef?: (idx: number, el: HTMLButtonElement | null) => void;
  onArrow?: (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => void;
}

export const MenuItem: React.FC<MenuItemInternalProps> = ({
  onClick,
  disabled,
  tone = 'default',
  children,
  closeMenu,
  itemIndex = 0,
  registerRef,
  onArrow,
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    registerRef?.(itemIndex, ref.current);
    return () => registerRef?.(itemIndex, null);
  }, [itemIndex, registerRef]);
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      disabled={disabled}
      className={`pat-menu__item${tone === 'danger' ? ' pat-menu__item--danger' : ''}`}
      onClick={() => {
        if (disabled) return;
        onClick();
        closeMenu?.();
      }}
      onKeyDown={(e) => onArrow?.(e, itemIndex)}
      tabIndex={-1}
    >
      {children}
    </button>
  );
};
MenuItem.displayName = 'MenuItem';

export interface MenuProps {
  /** The element that opens the menu when clicked. */
  trigger: ReactElement;
  children: ReactNode;
  /** Optional placement (defaults to 'bottom-start'). */
  placement?: 'bottom-start' | 'bottom-end';
}

const Menu: React.FC<MenuProps> = ({ trigger, children, placement = 'bottom-start' }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Compute viewport-fixed coords on open so the dropdown escapes ancestor
  // overflow: hidden (e.g. the DataTable wrapper clips the last row's menu).
  useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    const triggerEl = wrapperRef.current?.querySelector('[aria-haspopup="menu"]') as HTMLElement | null;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    setCoords(
      placement === 'bottom-end'
        ? { top: rect.bottom, right: window.innerWidth - rect.right }
        : { top: rect.bottom, left: rect.left }
    );
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    // Focus first non-disabled item when menu opens
    const firstEnabled = itemRefs.current.find((el) => el && !el.disabled);
    firstEnabled?.focus();

    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Fixed-positioned menu doesn't track ancestor scroll; close instead of drift.
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const registerRef = (idx: number, el: HTMLButtonElement | null) => {
    itemRefs.current[idx] = el;
  };

  const onArrow = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % items.length;
      items[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + items.length) % items.length;
      items[prev]?.focus();
    }
  };

  const triggerProps = trigger.props as { onClick?: (e: React.MouseEvent) => void };
  const triggerEl = React.cloneElement(trigger as ReactElement<Record<string, unknown>>, {
    onClick: (e: React.MouseEvent) => {
      triggerProps.onClick?.(e);
      setOpen((o) => !o);
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
  });

  const closeMenu = () => setOpen(false);
  let menuItemCount = 0;
  const decorated = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if ((child.type as React.FC).displayName !== 'MenuItem') return child;
    const currentIndex = menuItemCount++;
    return React.cloneElement(child as React.ReactElement<MenuItemInternalProps>, {
      closeMenu,
      itemIndex: currentIndex,
      registerRef,
      onArrow,
    });
  });

  const listStyle: React.CSSProperties = coords
    ? { position: 'fixed', top: coords.top, left: coords.left, right: coords.right }
    : { visibility: 'hidden' };

  return (
    <div ref={wrapperRef} className="pat-menu" data-testid="menu" data-placement={placement}>
      {triggerEl}
      {open && createPortal(
        <div
          ref={listRef}
          role="menu"
          className={`pat-menu__list pat-menu__list--${placement}`}
          style={listStyle}
        >
          {decorated}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Menu;
